import { useEffect, useRef, useState } from "react";

import {
  abortPasskeyEnrollment,
  continuePasskeyEnrollment,
  useUser,
} from "@auth0/auth0-acul-react/passkey-enrollment";

import { ULThemeButton } from "@/components/ULThemeButton";
import ULThemeCard from "@/components/ULThemeCard";
import ULThemeSubtitle from "@/components/ULThemeSubtitle";
import ULThemeTitle from "@/components/ULThemeTitle";
import { applyCareSuperBrand } from "@/utils/theme/careSuperBrand";

/**
 * The `passkey-enrollment` screen serves TWO different moments, and this file
 * has to behave differently in each:
 *
 *   SIGNUP  /u/signup/passkeys     -> suppress it entirely
 *   LOGIN   /u/passkey-enrollment  -> offer enrolment (progressive enrolment)
 *
 * Same prompt (`passkeys`), same screen name (`passkey-enrollment`), different
 * URLs. That shared identity is the whole reason this is not a plain
 * auto-skip: an unconditional abort suppresses BOTH, which is exactly the
 * regression this replaces — passkeys were enabled for sign-in and then
 * silently never offered, because the signup fix caught the login case too.
 *
 * WHY SIGNUP IS SUPPRESSED. The passkey path issues a credential INSTEAD of
 * the password step, so it skips the ACUL signup-password screen — the only
 * place the mandatory ulp-* fields are collected. "Capture Signup Profile
 * Fields" then rejects the registration and the member is told to "enter all
 * the required information" with no field on screen to supply. Confirmed in
 * the tenant logs: PreUserRegistrationValidationError missing_signup_fields,
 * body carrying "passkey": "*****", is_signup: true, and no ulp-* keys. That
 * path cannot succeed here, so it is declined automatically.
 *
 * WHY LOGIN IS NOT. Progressive enrolment after a password login is a wanted
 * feature and works fine — the member already exists, so nothing is missing.
 * In advanced rendering Auth0 does NOT draw its native screen for us, so the
 * offer below has to be rendered by this bundle or it does not exist at all.
 *
 * THE DISCRIMINATOR: `user.id`. At signup the member has not been created yet,
 * so the user context is empty; during progressive enrolment they are
 * authenticated and it is populated. Verified by dumping the live context at
 * both moments rather than reasoning about it:
 *
 *   signup -> user.id absent,  user.email absent
 *   login  -> user.id present, user.email present
 *
 * Deliberately NOT keyed on location.pathname. The URLs do differ and would
 * work today, but a URL is a presentation detail Auth0 can change; "does this
 * member exist yet" is the actual semantic difference between the two cases.
 */
function PasskeyEnrollmentScreen() {
  // Hooks must run unconditionally, before any branch.
  const user = useUser();
  const hasAborted = useRef(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  // Populated user == authenticated member == progressive enrolment at login.
  const isProgressiveEnrollment = Boolean(user?.id);

  useEffect(() => {
    if (isProgressiveEnrollment) return;
    if (hasAborted.current) return;
    hasAborted.current = true;

    // Signup: decline silently so the member goes straight to the wizard.
    Promise.resolve(abortPasskeyEnrollment()).catch(() => setFailed(true));
  }, [isProgressiveEnrollment]);

  if (!isProgressiveEnrollment) {
    // Render nothing — the member should perceive a redirect, not a screen.
    // Except on failure: a silent blank page is the worst outcome for a screen
    // whose whole job is to be invisible, so give a visible way out.
    if (failed) {
      return (
        <div className="flex min-h-screen items-center justify-center p-6">
          <ULThemeButton onClick={() => void abortPasskeyEnrollment()}>
            Continue
          </ULThemeButton>
        </div>
      );
    }
    return null;
  }

  applyCareSuperBrand();

  const handleCreate = async () => {
    setBusy(true);
    setFailed(false);
    try {
      await continuePasskeyEnrollment();
    } catch {
      // Most commonly the member dismissed the OS prompt. Not worth blocking
      // on — let them retry or skip.
      setFailed(true);
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <ULThemeCard className="theme-universal w-full max-w-md gap-4">
        <ULThemeTitle>Add a passkey</ULThemeTitle>
        <ULThemeSubtitle>
          Next time you can sign in with your fingerprint, face or device PIN
          instead of your password.
        </ULThemeSubtitle>

        {failed && (
          <p role="alert" className="text-sm">
            That didn&apos;t complete. You can try again, or skip for now.
          </p>
        )}

        <ULThemeButton
          className="w-full"
          disabled={busy}
          onClick={() => void handleCreate()}
        >
          {busy ? "Waiting for your device…" : "Create a passkey"}
        </ULThemeButton>

        <ULThemeButton
          variant="outline"
          className="w-full"
          disabled={busy}
          onClick={() => void abortPasskeyEnrollment()}
        >
          Not now
        </ULThemeButton>
      </ULThemeCard>
    </div>
  );
}

export default PasskeyEnrollmentScreen;
